import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { MAX_PRODUCT_PHOTOS, prepareProductPhoto } from '../utils/productPhotos';

const errorText = (e, fallback) => (typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallback);

// The app's own pages ask for a photo with the sign-in header, which an <img> can't send, so it is fetched and shown from memory.
const cache = new Map();
function useAuthImage(url) {
  const [src, setSrc] = useState(url ? cache.get(url) || null : null);
  useEffect(() => {
    if (!url) { setSrc(null); return undefined; }
    if (cache.has(url)) { setSrc(cache.get(url)); return undefined; }
    let cancelled = false;
    axios.get(url, { responseType: 'blob' })
      .then((r) => {
        if (cache.size > 300) cache.clear();
        const objectUrl = URL.createObjectURL(r.data);
        cache.set(url, objectUrl);
        if (!cancelled) setSrc(objectUrl);
      })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
  }, [url]);
  return src;
}

/** A small photo of a product, or nothing when it has none. */
export function ProductThumb({ productId, imageId, size = 40, version = 0, url = null }) {
  // A photo in the media bucket has a CDN address (no sign-in needed); an older one is fetched through the app
  const fetched = useAuthImage(!url && productId && imageId ? `/api/products/${productId}/images/${imageId}/file?size=thumb&v=${version}` : null);
  const src = url || fetched;
  if (!imageId) return <div style={{ width: size, height: size }} className="rounded bg-body-secondary" aria-hidden="true" />;
  return src
    ? <img src={src} alt="" width={size} height={size} className="rounded" style={{ objectFit: 'cover' }} />
    : <div style={{ width: size, height: size }} className="rounded bg-body-secondary" aria-hidden="true" />;
}

/**
 * A product's photos in its edit dialog: add up to five (from the phone's camera too), choose the main one (shown first, and in the
 * catalog), remove one. Each is shrunk here and re-encoded by the server.
 */
function ProductPhotos({ productId, canEdit = true }) {
  const [photos, setPhotos] = useState(null);
  const [busy, setBusy] = useState(false);
  const [max, setMax] = useState(MAX_PRODUCT_PHOTOS); // the server says how many (more once photos live in the media bucket)
  const [limits, setLimits] = useState({}); // and the smallest and a good photo size
  const input = useRef(null);

  useEffect(() => {
    axios.get('/api/product-images/settings').then((r) => {
      if (r.data?.maxPhotos) setMax(r.data.maxPhotos);
      if (r.data?.minSide) setLimits({ minSide: r.data.minSide, goodSide: r.data.goodSide });
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setPhotos((await axios.get(`/api/products/${productId}/images`)).data);
    } catch (e) {
      setPhotos([]);
    }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setBusy(true);
    let room = max - (photos?.length || 0);
    for (const file of files) {
      if (room <= 0) { toast.warn(`A product can have ${max} photos at most`); break; }
      try {
        const { file: ready, warnings } = await prepareProductPhoto(file, limits);
        const body = new FormData();
        body.append('file', ready);
        await axios.post(`/api/products/${productId}/images`, body);
        room -= 1;
        warnings.forEach((w) => toast.warn(w)); // added, but worth knowing
      } catch (err) {
        toast.error(err.response ? errorText(err, `Could not add ${file.name}`) : err.message);
      }
    }
    await load();
    setBusy(false);
  };

  const remove = async (photo) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.delete(`/api/products/${productId}/images/${photo.id}`);
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not remove the photo'));
    }
  };

  const makeMain = async (photo) => {
    try {
      setPhotos((await axios.post(`/api/products/${productId}/images/${photo.id}/make-main`)).data);
    } catch (err) {
      toast.error(errorText(err, 'Could not change the main photo'));
    }
  };

  if (photos === null) return <Spinner animation="border" size="sm" />;
  return (
    <div data-testid="product-photos">
      <div className="d-flex flex-wrap gap-3 mb-2">
        {photos.map((p, i) => (
          <div key={p.id} style={{ width: 110 }} className="text-center">
            <ProductThumb productId={productId} imageId={p.id} size={110} url={p.thumbUrl || null} />
            <div className="mt-1 small">
              {i === 0 ? <Badge bg="success">Main</Badge>
                : canEdit && <Button type="button" variant="link" size="sm" className="p-0" onClick={() => makeMain(p)}>Make main</Button>}
              {canEdit && <Button type="button" variant="link" size="sm" className="p-0 ms-2 text-danger" onClick={() => remove(p)}>Remove</Button>}
            </div>
          </div>
        ))}
        {photos.length === 0 && <div className="text-muted small">No photos yet.</div>}
      </div>
      {canEdit && (
        <>
          <input ref={input} type="file" accept="image/*" multiple hidden onChange={add} data-testid="product-photo-input" />
          <Button type="button" size="sm" variant="outline-primary" disabled={busy || photos.length >= max} onClick={() => input.current?.click()}>
            <i className="bi bi-camera me-1" />{busy ? 'Adding...' : 'Add photos'}
          </Button>
          <span className="small text-muted ms-2">{photos.length} of {max}. JPG or PNG, up to 6 MB; on a phone this opens the camera too.</span>
          <div className="small text-muted mt-1" data-testid="photo-guide">
            Use a clear photo of at least {limits.goodSide || 800} x {limits.goodSide || 800} pixels on a plain background. Square works best, because the shop shows square pictures.
            Photos under {limits.minSide || 400} pixels on the shorter side are not accepted. The first photo is the main one.
          </div>
        </>
      )}
    </div>
  );
}

export default ProductPhotos;
